import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  fetchInvoiceRecordDetail,
  fetchInvoiceRecordList,
} from "@/api/invoice";
import type { InvoiceRecordListPagination } from "@/api/invoice";
import { toastError } from "@/utils/toast";

import { mapDetailDataToInvoice, mapRecordToInvoice } from "./mapRecordToInvoice";
import type { Invoice } from "./types";
import InvoiceCard from "./components/InvoiceCard";
import InvoiceDetailModal from "./components/InvoiceDetailModal";
import InvoicesPageHeader from "./components/InvoicesPageHeader";

const PAGE_LIMIT = 10;

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  const [list, setList] = useState<Invoice[]>([]);
  const [pagination, setPagination] =
    useState<InvoiceRecordListPagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [detailLoading, setDetailLoading] = useState(false);

  const loadPage = useCallback(async (page: number, append: boolean) => {
    try {
      if (append) setLoadingMore(true);
      else {
        setLoading(true);
        setError(null);
      }
      const data = await fetchInvoiceRecordList({ page, limit: PAGE_LIMIT });
      const mapped = data.list.map(mapRecordToInvoice);
      setPagination(data.pagination);
      setList((prev) => (append ? [...prev, ...mapped] : mapped));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "加载失败";
      // ★ 开发环境：屏蔽 401 避免干扰调试
      if (import.meta.env.DEV && /401|Unauthorized/i.test(msg)) {
        console.warn("[invoices] 开发环境跳过 401:", msg);
        return;
      }
      setError(msg);
      toastError(msg);
      if (!append) setList([]);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadPage(1, false);
  }, [loadPage]);

  const openInvoiceDetail = useCallback(async (inv: Invoice) => {
    setSelectedInvoice(inv);
    if (typeof inv.recordId !== "number") {
      return;
    }
    setDetailLoading(true);
    try {
      const data = await fetchInvoiceRecordDetail(inv.recordId);
      setSelectedInvoice(mapDetailDataToInvoice(data));
    } catch (e: unknown) {
      toastError(e instanceof Error ? e.message : "加载详情失败");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const canLoadMore =
    pagination != null && pagination.page < pagination.total_pages;

  if (selectedInvoice) {
    return (
      <InvoiceDetailModal
        invoice={selectedInvoice}
        loading={detailLoading}
        onClose={() => {
          setSelectedInvoice(null);
          setDetailLoading(false);
        }}
      />
    );
  }

  return (
    <div
      className="min-h-screen pb-10"
      style={{
        background: "#F7F7F7",
        fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif",
      }}
    >
      <InvoicesPageHeader onBack={() => navigate(-1)} />

      <div className="px-4 py-4 space-y-3">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <i
              className="ri-loader-4-line animate-spin"
              style={{ fontSize: 32, color: "#D97706" }}
            />
            <span className="text-sm text-gray-400">加载中...</span>
          </div>
        ) : error && list.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 gap-4">
            <p className="text-sm text-gray-500 text-center">{error}</p>
            <button
              type="button"
              onClick={() => loadPage(1, false)}
              className="px-6 py-2.5 rounded-full text-sm font-medium text-white cursor-pointer"
              style={{ background: "linear-gradient(135deg,#D97706,#F59E0B)" }}
            >
              重试
            </button>
          </div>
        ) : list.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <i className="ri-file-list-3-line text-4xl text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">暂无发票申请记录</p>
          </div>
        ) : (
          <>
            {list.map((inv) => (
              <InvoiceCard
                key={inv.recordId ?? inv.id}
                invoice={inv}
                onView={() => openInvoiceDetail(inv)}
              />
            ))}
            {canLoadMore ? (
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => {
                  const next = (pagination?.page ?? 1) + 1;
                  loadPage(next, true);
                }}
                className="w-full py-3 rounded-2xl text-sm font-semibold text-gray-600 cursor-pointer disabled:opacity-50"
                style={{ background: "#fff", border: "1px solid #E5E7EB" }}
              >
                {loadingMore ? "加载中..." : "加载更多"}
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
